import type { KeyboardEventHandler } from 'react';
import { useIntl } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import { maxLength, regex } from '../../utils/validation';
import type { FieldConfig } from '../field';

export const ZIP_CODE_FIELD = 'zipCode';

export function useZipCodeField(
    form: UseFormReturn<{ [ZIP_CODE_FIELD]: string }>,
    onKeyDown: KeyboardEventHandler<HTMLInputElement>,
): FieldConfig {
    const {
        control,
        formState: { errors },
    } = form;
    const intl = useIntl();

    return {
        id: 'zip-code-field',
        name: ZIP_CODE_FIELD,
        helpText: 'e.g. 52404',
        autoComplete: 'postal-code',
        label: intl.formatMessage({
            id: 'zip.code.field.label',
            defaultMessage: 'Enter Zip Code',
        }),
        type: 'text',
        rules: {
            required: intl.formatMessage({
                id: 'zip.code.field.required',
                defaultMessage: 'Please enter a Zip code',
            }),
            minLength: {
                value: maxLength.zipCode,
                message: intl.formatMessage(
                    {
                        id: 'zip.code.length',
                        defaultMessage: 'Zip code must be {length} characters long',
                    },
                    { length: maxLength.zipCode },
                ),
            },
            maxLength: {
                value: maxLength.zipCode,
                message: intl.formatMessage(
                    {
                        id: 'zip.code.length',
                        defaultMessage: 'Zip code must be {length} characters long',
                    },
                    { length: maxLength.zipCode },
                ),
            },
        },
        error: errors[ZIP_CODE_FIELD],
        variant: 'new-style',
        inputProps: ({ onChange }: { onChange: (phone: string) => void }) => ({
            inputMode: 'numeric',
            onChange(evt: React.ChangeEvent<HTMLInputElement>): void {
                const value = evt.target.value.replace(regex.onlyNumbers, '');

                if (value.length > maxLength.zipCode) {
                    evt.preventDefault();
                } else {
                    onChange(value);
                }
            },
            onKeyDown,
        }),
        autoFocus: false,
        control,
    };
}
